import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useBudgetActionCreateEditDispatch, useBudgetActionCreateEditState } from './budget-action-create-edit-context';
import BudgetActionAppService from '../../../../application/platform/sge/budget-action/budget-action-app-service';
import BudgetProgramAppService from '../../../../application/platform/sge/budget-program/budget-program-app-service';
import BudgetUnitAppService from '../../../../application/platform/sge/budget-unit/budget-unit-app-service';
import { useBaseLayout } from '../../../../components/context/use-base-layout';

export const useBudgetActionCreateEditController = () => {
    const navigate = useNavigate();
    const dispatch = useBudgetActionCreateEditDispatch();
    const state = useBudgetActionCreateEditState();
    const { showMessage, showLoading } = useBaseLayout();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        async function loadInitialData() {
            showLoading({ open: true });
            try {
                const budgetUnits = await BudgetUnitAppService.list();
                dispatch({ type: ACTION_TYPES.setBudgetUnits, payload: budgetUnits });

                if (state.budgetActionId) {
                    const data = await BudgetActionAppService.getById(Number(state.budgetActionId));
                    dispatch({
                        type: ACTION_TYPES.setBudgetActionRequest,
                        payload: { budgetProgramId: data.budgetProgramId, actionType: data.actionType, code: data.code, name: data.name }
                    });
                    const program = await BudgetProgramAppService.getById(data.budgetProgramId);
                    dispatch({ type: ACTION_TYPES.setSelectedBudgetUnitId, payload: program.budgetUnitId });
                }
            } catch (error: any) {
                showMessage({ open: true, message: 'Não foi possível carregar os dados da ação orçamentária', detail: error?.details || error?.message, type: 'error' });
            } finally {
                showLoading({ open: false });
            }
        }
        loadInitialData();
    }, [state.budgetActionId]);

    useEffect(() => {
        if (state.selectedBudgetUnitId) {
            BudgetProgramAppService.list({ budgetUnitId: state.selectedBudgetUnitId }).then((data) =>
                dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: data })
            );
        } else {
            dispatch({ type: ACTION_TYPES.setBudgetPrograms, payload: [] });
        }
    }, [state.selectedBudgetUnitId]);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        const parsed = ['budgetProgramId', 'actionType'].includes(name)
            ? (value ? parseInt(value, 10) : undefined)
            : value;
        dispatch({ type: ACTION_TYPES.updateBudgetActionRequest, payload: { [name]: parsed } });
    }

    function handleBudgetUnitChange(e: any) {
        const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
        dispatch({ type: ACTION_TYPES.setSelectedBudgetUnitId, payload: value });
        dispatch({ type: ACTION_TYPES.updateBudgetActionRequest, payload: { budgetProgramId: undefined } });
    }

    async function handleSave() {
        const { budgetProgramId, actionType, code, name } = state.budgetActionRequest;
        if (!budgetProgramId || !actionType || !code || !name) {
            dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
            return;
        }
        setIsSaving(true);
        try {
            if (state.budgetActionId) {
                await BudgetActionAppService.edit(Number(state.budgetActionId), state.budgetActionRequest);
                showMessage({ open: true, message: 'Ação orçamentária atualizada com sucesso!', type: 'success' });
            } else {
                await BudgetActionAppService.add(state.budgetActionRequest);
                showMessage({ open: true, message: 'Ação orçamentária adicionada com sucesso!', type: 'success' });
            }
            setTimeout(() => navigate('/acoes-orcamentarias'), 1500);
        } catch (error: any) {
            showMessage({ open: true, message: 'Não foi possível salvar a ação orçamentária', detail: error?.details || error?.message, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    }

    function handleCancel() {
        navigate('/acoes-orcamentarias');
    }

    return { handleOnChange, handleBudgetUnitChange, handleSave, handleCancel, isSaving };
};
